/**
 * Table sorting utilities and types
 * Provides comprehensive sorting capabilities for table data
 */

// Sort types and interfaces
export type SortDirection = 'asc' | 'desc' | null;

export type SortDataType = 
  | 'string' 
  | 'number' 
  | 'date' 
  | 'boolean' 
  | 'custom';

export interface SortColumn {
  id: string;
  columnKey: string;
  direction: SortDirection;
  priority: number; // For multi-column sorting (lower number = higher priority)
  dataType: SortDataType;
  customSort?: (a: any, b: any) => number; // Custom sort function
}

export interface SortState {
  columns: SortColumn[];
  multiSort: boolean; // Allow multiple columns to be sorted
}

/**
 * Create a default sort state
 */
export const createDefaultSortState = (): SortState => ({
  columns: [],
  multiSort: false
});

/**
 * Create initial sort state (alias for createDefaultSortState)
 */
export const createInitialSortState = createDefaultSortState;

/**
 * Create a sort column configuration
 */
export const createSortColumn = (
  columnKey: string,
  dataType: SortDataType = 'string',
  customSort?: (a: any, b: any) => number
): SortColumn => ({
  id: `sort_${columnKey}`,
  columnKey,
  direction: null,
  priority: 0,
  dataType,
  customSort
});

/**
 * Toggle sort direction for a column
 */
export const toggleSort = (sortState: SortState, columnKey: string): SortState => {
  const existingIndex = sortState.columns.findIndex(col => col.columnKey === columnKey);
  
  if (existingIndex === -1) {
    // No existing sort for this column, add ascending
    const newColumn: SortColumn = {
      id: `sort_${columnKey}`,
      columnKey,
      direction: 'asc',
      priority: sortState.multiSort ? sortState.columns.length : 0,
      dataType: 'string' // Default, should be configured per column
    };

    return {
      ...sortState,
      columns: sortState.multiSort 
        ? [...sortState.columns, newColumn]
        : [newColumn]
    };
  }

  const existingColumn = sortState.columns[existingIndex];
  
  if (existingColumn.direction === 'asc') {
    // Change to descending
    return {
      ...sortState,
      columns: sortState.columns.map(col => 
        col.columnKey === columnKey
          ? { ...col, direction: 'desc' as SortDirection }
          : col
      )
    };
  } else if (existingColumn.direction === 'desc') {
    // Remove sort
    const newColumns = sortState.columns.filter(col => col.columnKey !== columnKey);
    // Recalculate priorities
    return {
      ...sortState,
      columns: newColumns.map((col, index) => ({ ...col, priority: index }))
    };
  }

  return sortState;
};

/**
 * Set sort for a specific column
 */
export const setSort = (
  sortState: SortState, 
  columnKey: string, 
  direction: SortDirection,
  dataType: SortDataType = 'string',
  customSort?: (a: any, b: any) => number
): SortState => {
  const existingIndex = sortState.columns.findIndex(col => col.columnKey === columnKey);
  
  if (direction === null) {
    // Remove sort
    if (existingIndex === -1) return sortState;
    
    const newColumns = sortState.columns.filter(col => col.columnKey !== columnKey);
    return {
      ...sortState,
      columns: newColumns.map((col, index) => ({ ...col, priority: index }))
    };
  }

  const sortColumn: SortColumn = {
    id: `sort_${columnKey}`,
    columnKey,
    direction,
    priority: sortState.multiSort ? sortState.columns.length : 0,
    dataType,
    customSort
  };

  if (existingIndex === -1) {
    // Add new sort
    return {
      ...sortState,
      columns: sortState.multiSort 
        ? [...sortState.columns, sortColumn]
        : [sortColumn]
    };
  } else {
    // Update existing sort
    return {
      ...sortState,
      columns: sortState.columns.map(col => 
        col.columnKey === columnKey
          ? { ...col, direction, dataType, customSort }
          : col
      )
    };
  }
};

/**
 * Clear all sorts
 */
export const clearSorts = (sortState: SortState): SortState => ({
  ...sortState,
  columns: []
});

/**
 * Clear sort for a specific column
 */
export const clearSort = (sortState: SortState, columnKey: string): SortState => {
  const newColumns = sortState.columns.filter(col => col.columnKey !== columnKey);
  return {
    ...sortState,
    columns: newColumns.map((col, index) => ({ ...col, priority: index }))
  };
};

/**
 * Reorder sort priorities (for drag-and-drop reordering)
 */
export const reorderSorts = (sortState: SortState, fromIndex: number, toIndex: number): SortState => {
  const newColumns = [...sortState.columns];
  const [movedColumn] = newColumns.splice(fromIndex, 1);
  newColumns.splice(toIndex, 0, movedColumn);
  
  return {
    ...sortState,
    columns: newColumns.map((col, index) => ({ ...col, priority: index }))
  };
};

/**
 * Enable/disable multi-sort
 */
export const setMultiSort = (sortState: SortState, enabled: boolean): SortState => {
  if (!enabled && sortState.columns.length > 1) {
    // Keep only the first (highest priority) sort
    return {
      ...sortState,
      multiSort: false,
      columns: sortState.columns.slice(0, 1).map(col => ({ ...col, priority: 0 }))
    };
  }
  
  return {
    ...sortState,
    multiSort: enabled
  };
};

/**
 * Get sort direction for a column
 */
export const getSortDirection = (sortState: SortState, columnKey: string): SortDirection => {
  const column = sortState.columns.find(col => col.columnKey === columnKey);
  return column?.direction || null;
};

/**
 * Get sort priority for a column (1-based for display)
 */
export const getSortPriority = (sortState: SortState, columnKey: string): number | null => {
  const column = sortState.columns.find(col => col.columnKey === columnKey);
  return column ? column.priority + 1 : null;
};

/**
 * Normalize value for sorting based on data type
 */
const normalizeForSort = (value: any, dataType: SortDataType): any => {
  // Handle null/undefined values - they should sort to the end
  if (value === null || value === undefined) {
    return dataType === 'number' ? -Infinity : '';
  }

  switch (dataType) {
    case 'number':
      const num = Number(value);
      return isNaN(num) ? -Infinity : num;
    
    case 'date':
      const date = new Date(value);
      return isNaN(date.getTime()) ? new Date(0) : date;
    
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value === '1' || value === 'yes';
      }
      return Boolean(value);
    
    case 'string':
    default:
      return String(value).toLowerCase();
  }
};

/**
 * Compare two values for sorting
 */
export const compareValues = (
  a: any, 
  b: any, 
  dataType: SortDataType, 
  direction: SortDirection,
  customSort?: (a: any, b: any) => number
): number => {
  if (direction === null) return 0;

  // Use custom sort function if provided
  if (customSort) {
    const result = customSort(a, b);
    return direction === 'desc' ? -result : result;
  }

  const normalizedA = normalizeForSort(a, dataType);
  const normalizedB = normalizeForSort(b, dataType);

  let comparison = 0;

  if (normalizedA < normalizedB) {
    comparison = -1;
  } else if (normalizedA > normalizedB) {
    comparison = 1;
  }

  return direction === 'desc' ? -comparison : comparison;
};

/**
 * Apply sorting to a dataset
 */
export const applySorting = <T extends Record<string, any>>(
  data: T[],
  sortState: SortState
): T[] => {
  if (sortState.columns.length === 0) {
    return [...data];
  }

  // Sort columns by priority (lower priority number = higher precedence)
  const sortColumns = [...sortState.columns].sort((a, b) => a.priority - b.priority);

  return [...data].sort((a, b) => {
    for (const sortColumn of sortColumns) {
      const aValue = a[sortColumn.columnKey];
      const bValue = b[sortColumn.columnKey];
      
      const comparison = compareValues(
        aValue, 
        bValue, 
        sortColumn.dataType, 
        sortColumn.direction,
        sortColumn.customSort
      );
      
      if (comparison !== 0) {
        return comparison;
      }
    }
    
    return 0;
  });
};

/**
 * Get sort summary for display
 */
export const getSortSummary = (sortState: SortState): string => {
  if (sortState.columns.length === 0) {
    return 'No sorting applied';
  }

  if (sortState.columns.length === 1) {
    const col = sortState.columns[0];
    return `Sorted by ${col.columnKey} (${col.direction === 'asc' ? 'ascending' : 'descending'})`;
  }

  return `Sorted by ${sortState.columns.length} columns`;
};

/**
 * Get sort instructions for screen readers
 */
export const getSortInstructions = (sortState: SortState, columnKey: string): string => {
  const currentDirection = getSortDirection(sortState, columnKey);
  const priority = getSortPriority(sortState, columnKey);
  
  if (currentDirection === null) {
    return 'Click to sort ascending';
  } else if (currentDirection === 'asc') {
    return `Currently sorted ascending${priority ? ` (priority ${priority})` : ''}. Click to sort descending`;
  } else {
    return `Currently sorted descending${priority ? ` (priority ${priority})` : ''}. Click to remove sort`;
  }
};

/**
 * Detect data type from sample values
 */
export const detectDataType = (values: any[]): SortDataType => {
  // Filter out null/undefined values for detection
  const validValues = values.filter(val => val !== null && val !== undefined && val !== '');
  
  if (validValues.length === 0) return 'string';

  // Check if all values are numbers
  if (validValues.every(val => !isNaN(Number(val)))) {
    return 'number';
  }

  // Check if all values are dates
  if (validValues.every(val => !isNaN(new Date(val).getTime()))) {
    return 'date';
  }

  // Check if all values are booleans
  if (validValues.every(val => 
    typeof val === 'boolean' || 
    (typeof val === 'string' && ['true', 'false', '1', '0', 'yes', 'no'].includes(val.toLowerCase()))
  )) {
    return 'boolean';
  }

  return 'string';
};

/**
 * Auto-detect sort configurations for columns
 */
export const autoDetectSortConfigs = <T extends Record<string, any>>(
  data: T[],
  columnKeys: string[]
): Record<string, SortDataType> => {
  const configs: Record<string, SortDataType> = {};
  
  for (const key of columnKeys) {
    const values = data.slice(0, 100).map(row => row[key]); // Sample first 100 rows
    configs[key] = detectDataType(values);
  }
  
  return configs;
};

/**
 * Serialize sort state to URL-safe string
 */
export const serializeSortState = (sortState: SortState): string => {
  try {
    return btoa(JSON.stringify(sortState));
  } catch {
    return '';
  }
};

/**
 * Deserialize sort state from URL-safe string
 */
export const deserializeSortState = (serialized: string): SortState | null => {
  try {
    return JSON.parse(atob(serialized));
  } catch {
    return null;
  }
};